/* tslint:disable:indent */
import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {EformControl} from '../../../shared/models/eformControl';
import {Notice} from '../model/notice';
import {EformConditionsService} from './eform-conditions.service';
import {EformDataService} from "./eform-data.service";
import {DuplicableObjectArrayEnum} from "../enum/duplicable-object-array.enum";

@Injectable({
  providedIn: 'root'
})
export class EformsSubjectService {
  private isUpdatingField = false; // Flag pour éviter les cycles

  constructor(private eformDataService: EformDataService, private eformConditionService: EformConditionsService) {
  }

  public notice: Notice;
  public idSchemeListSubject = new BehaviorSubject<string[]>([]);
  public changeCorrigJustification = new BehaviorSubject<string[]>([]);

  setIdSchemeListSubject(array: string[]): void {
    this.idSchemeListSubject.next(array);
  }

  getField(pathElements, parentForm, repeatablePathWithIndex, field?) {
    let tempControl;
    let i = 0;
    let conditions = field?.conditionsCreationMap;
    pathElements.forEach(elements => {
      if (i == 0) {
        tempControl = elements.includes('[]') ? (<any>parentForm).controls[elements.replace('[]', '')].controls[this.eformDataService.getArrayIndex(elements, repeatablePathWithIndex)]
          : parentForm.controls[elements];
      } else {
        if (elements?.includes('[]')) {
          let index = this.eformDataService.getArrayIndex(elements, repeatablePathWithIndex);
          if (conditions && elements.replace('[]', '') in conditions) {
            index = tempControl?.controls[elements.replace('[]', '')]?.controls.findIndex((c => this.eformConditionService.verifyConditionsCreationWithControls(c, c.conditionsCreation, conditions[elements.replace('[]', '')])));
            index = index != -1 ? index : 0
          }
          tempControl = tempControl?.controls[elements.replace('[]', '')]?.controls[index];
        } else {
          tempControl = tempControl?.controls[elements];
        }
      }
      i++;
    });
    return tempControl;
  }

  deleteField(indexArray, repeatablePathWithIndex, pathElements: Array<string>, lastArrayElement, form) {
    let currentElement: any = form;
    for (const index in pathElements) {
      if (lastArrayElement === pathElements[index]) {
        currentElement.controls[pathElements[index].replace('[]', '')].removeAt(indexArray);
        break;
      } else {
        if (pathElements[index]?.includes('[]')) {
          currentElement = currentElement.controls[pathElements[index].replace('[]', '')].get(this.eformDataService.getArrayIndex(pathElements[index], repeatablePathWithIndex))
        } else {
          currentElement = currentElement.controls[pathElements[index]]
        }
      }
    }
  }

  updateField(repeatablePathWithIndex, jsonPath, form, nameAttribut, valueAttribut, type, field?) {

    // Prévenir les cycles infinis - si une mise à jour est déjà en cours, ignorer
    if (this.isUpdatingField) {
      console.debug('[EformsSubjectService] updateField ignoré - mise à jour déjà en cours');
      return;
    }

    this.isUpdatingField = true;


    try {
      let currentElement: any = form;
      const pathElements: Array<string> = jsonPath.split('.');
      const conditions = field?.conditionsCreationMap;
      const fieldListName = field?.listname;

      for (let i = 0; i < pathElements.length; i++) {
        const element = pathElements[i];
        const isLastElement = i === pathElements.length - 1;

        if (isLastElement) {
          // Dernier élément : on met à jour la valeur
          if (currentElement?.controls[nameAttribut]) {
            currentElement.controls[nameAttribut].setValue(valueAttribut, {emitEvent: false});
          } else if (currentElement) {
            currentElement.setControl(nameAttribut, new EformControl(null, null, type, valueAttribut));
          }
        } else if (element?.includes('[]')) {
          const arrayName = element.replace('[]', '');
          const arrayNameAsEnum = arrayName as unknown as DuplicableObjectArrayEnum;

          if (Object.values(DuplicableObjectArrayEnum).includes(arrayNameAsEnum)) {
            const duplicableControls = currentElement?.controls[arrayName]?.controls;
            // 🔧 FIX : Pour les tableaux duplicables (ex: procurementprojectlot), si un
            // conditionsCreationMap est défini et qu'il y a plusieurs entrées, utiliser
            // les conditions pour sélectionner le bon élément.
            // Priorité 1 : contrôle 'schemeName' stocké directement depuis field._schemeName.
            // Priorité 2 : fallback sur comparaison des conditionsCreation (chaînes).
            if (conditions && arrayName in conditions && duplicableControls?.length > 1) {
              const conditionStrings: string[] = conditions[arrayName];
              const schemeNameExpected = conditionStrings
                ?.map(c => { const m = c.match(/&schemeName='([^']+)'/i); return m ? m[1] : null; })
                ?.find(v => v !== null);

              let foundIndex = -1;

              if (schemeNameExpected) {
                // Cherche le contrôle 'schemeName' stocké par createEformsElement depuis field._schemeName
                foundIndex = duplicableControls.findIndex(c =>
                  c?.controls?.['schemeName']?.value === schemeNameExpected
                );
                // Fallback sur l'ancien contrôle '_schemeName' (compatibilité)
                if (foundIndex === -1) {
                  foundIndex = duplicableControls.findIndex(c =>
                    c?.controls?.['_schemeName']?.value === schemeNameExpected
                  );
                }
              }

              // Fallback : comparaison des conditionsCreation stockées sur le FormGroup
              if (foundIndex === -1) {
                foundIndex = duplicableControls.findIndex(
                  c => this.eformConditionService.verifyConditionsCreationWithControls(c, c.conditionsCreation, conditionStrings)
                );
              }

              currentElement = duplicableControls?.[foundIndex !== -1 ? foundIndex : 0];
            } else {
              currentElement = duplicableControls?.[0];
            }
          } else {
            const arrayControls = currentElement?.controls[arrayName]?.controls;

            if (!arrayControls || arrayControls.length === 0) {
              currentElement = null;
              break;
            }


            // 1. D'abord essayer getArrayIndex (index explicite dans le path)
            let arrayIndex = -1;
            const indexFromPath = this.eformDataService.getArrayIndex(element, repeatablePathWithIndex);

            if (indexFromPath >= 0 && indexFromPath < arrayControls.length) {
              arrayIndex = indexFromPath;
            }

            // 2. Si pas trouvé, essayer par conditionsCreationMap
            if (arrayIndex === -1 && conditions && arrayName in conditions) {
              const foundIndex = arrayControls.findIndex(
                (c => this.eformConditionService.verifyConditionsCreationWithControls(c, c.conditionsCreation, conditions[arrayName]))
              );
              if (foundIndex !== -1) {
                arrayIndex = foundIndex;
              }
            }

            // 3. Si toujours pas trouvé, chercher par listName
            if (arrayIndex === -1 && fieldListName) {
              const foundIndex = arrayControls.findIndex(g => {
                const groupListName = g?.get('listName')?.value;
                if (groupListName && groupListName.toLowerCase() === fieldListName.toLowerCase()) {
                  return true;
                }
                if (g?.conditionsCreation) {
                  const conditionsStr = JSON.stringify(g.conditionsCreation).toLowerCase();
                  return conditionsStr.includes(`listname='${fieldListName.toLowerCase()}'`);
                }
                return false;
              });
              if (foundIndex !== -1) {
                arrayIndex = foundIndex;
              }
            }

            // 4. Si toujours pas d'index valide, utiliser 0
            if (arrayIndex < 0 || arrayIndex >= arrayControls.length) {
              arrayIndex = 0;
            }

            currentElement = arrayControls[arrayIndex];
          }
        } else {
          currentElement = currentElement?.controls[element];
        }

        if (!currentElement) {
          break;
        }
      }
    } finally {
      this.isUpdatingField = false;
    }
  }
}
